import React, { useState, useEffect } from "react";
import { Modal, FormInput, Button } from "@/components/ui";
import { showApiError, showSuccessMessage } from "@/lib/toast";
import { statusRevertService } from "../services/statusRevertService";
import { CONTACT_STAGES } from "../constants/contactConstants";
import { AlertTriangle } from "lucide-react";

export const RequestRevertModal = ({
  isOpen,
  onClose,
  contact,
  lead,
  targetStatus,
  onRequested,
  isOwner = false,
  onDirectRevert,
  stages = CONTACT_STAGES
}) => {
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWarning, setShowWarning] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setShowWarning(true);
      setReason("");
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const targetLabel = stages.find(s => s.key === targetStatus)?.label || targetStatus;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return;

    try {
      setIsSubmitting(true);
      const recordId = contact?._id || lead?._id;
      const currentStatus = contact?.status || lead?.leadStatus || lead?.status;
      
      const payload = {
        contactId: recordId,
        currentStatus,
        targetStatus,
        reason,
      };

      await statusRevertService.createRequest(payload);
      showSuccessMessage("Revert request submitted successfully and sent for approval.");
      if (onRequested) onRequested();
      onClose();
    } catch (error) {
      showApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showWarning) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Warning"
        size="md"
        footer={
          <div className="flex justify-end gap-3 w-full">
            <Button variant="outline" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (isOwner && onDirectRevert) {
                  onDirectRevert();
                } else {
                  setShowWarning(false);
                }
              }}
            >
              Confirm
            </Button>
          </div>
        }
      >
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-bold text-slate-800 mb-2">Are you sure you want to continue?</h3>
          <p className="text-slate-600 text-sm max-w-[320px]">
            You are trying to revert the status. This is a critical action that requires approval and may affect ongoing processes.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Request Stage Revert"
      size="md"
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button variant="outline" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="revert-form"
            loading={isSubmitting}
            disabled={!reason.trim()}
          >
            Submit Request
          </Button>
        </div>
      }
    >
      <form id="revert-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="bg-amber-50 text-amber-800 p-3 rounded-lg text-sm mb-2 border border-amber-200">
          <strong>Note:</strong> Moving a {lead ? 'lead' : 'contact'} to a previous stage requires Owner/Admin approval.
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <FormInput
            label="Verve ID"
            value={contact?.customId || lead?.customId || "N/A"}
            readOnly
            disabled
          />
          <FormInput
            label={lead ? "Lead Name" : "Customer Name"}
            value={(contact || lead)?.customerName || `${(contact || lead)?.firstName || ""} ${(contact || lead)?.lastName || ""}`}
            readOnly
            disabled
          />
        </div>

        <FormInput
          label="Target Status"
          value={targetLabel}
          readOnly
          disabled
        />

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-slate-700">Reason for Revert <span className="text-red-500">*</span></label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-brand-blue/20 resize-none h-24"
            placeholder={`Explain why you are requesting to move this ${lead ? 'lead' : 'contact'} back to a previous stage...`}
            required
          />
        </div>
      </form>
    </Modal>
  );
};
